import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface RitualPhrase {
  id: string;
  phrase: string;
  label: string | null;
  emoji: string | null;
  category: string | null;
  world: string | null;
  useCount: number;
  favorite: boolean;
}

interface RitualPhrasePanelProps {
  companionId: string;
  onInsert?: (phrase: string) => void;
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'opening', label: '🌅 Opening', emoji: '🌅' },
  { value: 'closing', label: '🌙 Closing', emoji: '🌙' },
  { value: 'transition', label: '🌊 Transition', emoji: '🌊' },
  { value: 'grounding', label: '🍃 Grounding', emoji: '🍃' },
  { value: 'invocation', label: '🕯️ Invocation', emoji: '🕯️' },
  { value: 'blessing', label: '✨ Blessing', emoji: '✨' },
];

export default function RitualPhrasePanel({
  companionId,
  onInsert,
  onClose,
}: RitualPhrasePanelProps) {
  const { t } = useTranslation();
  const [phrases, setPhrases] = useState<RitualPhrase[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Add form
  const [newPhrase, setNewPhrase] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newEmoji, setNewEmoji] = useState('🕯️');
  const [newCategory, setNewCategory] = useState('');
  const [newWorld, setNewWorld] = useState('');

  const fetchPhrases = useCallback(async () => {
    try {
      const params: Record<string, string> = { companionId };
      if (filterCategory) params.category = filterCategory;
      if (search) params.search = search;
      const res = await axios.get('/ritual-phrases', { params });
      setPhrases(res.data);
    } catch (err) {
      console.error('Failed to fetch ritual phrases', err);
    } finally {
      setLoading(false);
    }
  }, [companionId, filterCategory, search]);

  useEffect(() => {
    fetchPhrases();
  }, [fetchPhrases]);

  const handleAdd = async () => {
    if (!newPhrase.trim()) return;
    try {
      await axios.post('/ritual-phrases', {
        phrase: newPhrase.trim(),
        label: newLabel.trim() || undefined,
        emoji: newEmoji || '🕯️',
        category: newCategory || undefined,
        world: newWorld.trim() || undefined,
        companionId,
      });
      setNewPhrase('');
      setNewLabel('');
      setNewEmoji('🕯️');
      setNewCategory('');
      setNewWorld('');
      setShowAdd(false);
      fetchPhrases();
    } catch (err) {
      console.error('Failed to add phrase', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/ritual-phrases/${id}`);
      fetchPhrases();
    } catch (err) {
      console.error('Failed to delete phrase', err);
    }
  };

  const handleToggleFavorite = async (phrase: RitualPhrase) => {
    try {
      await axios.patch(`/ritual-phrases/${phrase.id}`, {
        favorite: !phrase.favorite,
      });
      fetchPhrases();
    } catch (err) {
      console.error('Failed to toggle favorite', err);
    }
  };

  const handleInsert = async (phrase: RitualPhrase) => {
    try {
      await axios.post(`/ritual-phrases/${phrase.id}/use`);
    } catch (_) {}
    if (onInsert) onInsert(phrase.phrase);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end">
      <div className="w-full max-w-md bg-gray-900 h-full overflow-y-auto p-4 animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            🕯️ {t('ritualPhrases', 'Ritual Phrases')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">
            ✕
          </button>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-2 mb-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPhrases', 'Search phrases...')}
            className="flex-1 bg-gray-800 text-white rounded px-3 py-2 text-sm border border-gray-700 focus:border-purple-500 outline-none"
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-gray-800 text-white rounded px-2 py-2 text-sm border border-gray-700"
          >
            <option value="">{t('allCategories', 'All')}</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Add Button */}
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="w-full mb-3 py-2 rounded bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium"
        >
          {showAdd ? t('cancel', 'Cancel') : `+ ${t('addPhrase', 'Add Phrase')}`}
        </button>

        {/* Add Form */}
        {showAdd && (
          <div className="bg-gray-800 rounded p-3 mb-4 space-y-2">
            <textarea
              value={newPhrase}
              onChange={(e) => setNewPhrase(e.target.value)}
              placeholder={t('ritualPhraseText', 'The phrase itself...')}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm resize-none border border-gray-600 focus:border-purple-500 outline-none"
              rows={3}
            />
            <div className="flex gap-2">
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t('label', 'Label')}
                className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
              />
              <input
                value={newEmoji}
                onChange={(e) => setNewEmoji(e.target.value)}
                placeholder="🕯️"
                className="w-14 bg-gray-700 text-white rounded px-2 py-2 text-sm text-center border border-gray-600"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
              >
                <option value="">{t('selectCategory', 'Category...')}</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                value={newWorld}
                onChange={(e) => setNewWorld(e.target.value)}
                placeholder={t('world', 'World')}
                className="flex-1 bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
              />
            </div>
            <button
              onClick={handleAdd}
              disabled={!newPhrase.trim()}
              className="w-full py-2 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-medium"
            >
              {t('savePhrase', 'Save Phrase')}
            </button>
          </div>
        )}

        {/* Phrase List */}
        {loading ? (
          <p className="text-gray-500 text-sm text-center py-8">{t('loading', 'Loading...')}</p>
        ) : phrases.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {t('noRitualPhrases', 'No ritual phrases yet.')}
            </p>
            <p className="text-gray-600 text-xs mt-1">
              {t(
                'ritualPhrasesHint',
                'Save phrases you use in openings, closings, transitions — they become part of your ritual language.',
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {phrases.map((p) => (
              <div
                key={p.id}
                className="bg-gray-800 rounded p-3 group hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {p.label && (
                      <div className="text-xs text-purple-400 mb-1">
                        {p.emoji || '🕯️'} {p.label}
                        {p.world && <span className="ml-2 text-gray-500">· {p.world}</span>}
                      </div>
                    )}
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{p.phrase}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {p.category && (
                        <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                          {CATEGORIES.find((c) => c.value === p.category)?.emoji} {p.category}
                        </span>
                      )}
                      {p.useCount > 0 && (
                        <span className="text-xs text-gray-600">
                          {t('used', 'used')} {p.useCount}×
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1 opacity-60 group-hover:opacity-100">
                    <button
                      onClick={() => handleToggleFavorite(p)}
                      className={`text-sm ${p.favorite ? 'text-yellow-400' : 'text-gray-500 hover:text-yellow-400'}`}
                      title={t('favorite', 'Favorite')}
                    >
                      {p.favorite ? '★' : '☆'}
                    </button>
                    {onInsert && (
                      <button
                        onClick={() => handleInsert(p)}
                        className="text-sm text-purple-400 hover:text-purple-300"
                        title={t('insertPhrase', 'Insert into message')}
                      >
                        ↵
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-sm text-gray-500 hover:text-red-400"
                      title={t('delete', 'Delete')}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
